import { Award } from 'lucide-react';
import { Card } from '@/shared/ui/card';
import type { Passbook } from '@/shared/types';
import { cn } from '@/shared/lib/cn';
import type { ParticipantTier } from '../api/participant-api';
import { formatTierProgress, TIER_TEXT_CLASS, tierTextStyle } from '../model/tier';

interface TierStatusCardProps {
  tier: ParticipantTier;
  program: Passbook;
}

/**
 * Bloco do nível no Início: nome, barra até o próximo e (se houver) benefícios
 * curtos do verso do cartão. Só renderiza quando a loja tem `hasTiers`.
 */
export function TierStatusCard({ tier, program }: TierStatusCardProps) {
  const accent = tier.color || tier.backgroundColor || 'hsl(var(--primary))';
  const textStyle = tierTextStyle(accent);
  const width = `${Math.round(tier.progress * 100)}%`;

  return (
    <Card className="relative overflow-hidden p-5 sm:p-6">
      <span
        className="pointer-events-none absolute inset-0 opacity-[0.14]"
        style={{ background: `radial-gradient(circle at 100% 0%, ${accent}, transparent 55%)` }}
      />
      <div className="relative space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Seu nível</p>
            <p className={cn('mt-0.5 text-2xl font-semibold tracking-tight', TIER_TEXT_CLASS)} style={textStyle}>
              {tier.label}
            </p>
          </div>
          <span
            className="grid size-11 shrink-0 place-items-center rounded-2xl ring-1 ring-black/5"
            style={{ backgroundColor: accent, color: tier.foregroundColor || '#fff' }}
            aria-hidden
          >
            <Award className="size-5" />
          </span>
        </div>

        <div>
          <div className="h-2 overflow-hidden rounded-full bg-muted">
            <div
              className={cn('h-full rounded-full transition-[width]', TIER_TEXT_CLASS)}
              style={{ width, backgroundColor: 'currentColor', ...textStyle }}
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={Math.round(tier.progress * 100)}
              aria-label={`Progresso no nível ${tier.label}`}
            />
          </div>
          <p className="mt-2 text-sm text-muted-foreground">{formatTierProgress(tier, program)}</p>
        </div>

        {tier.benefits ? <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">{tier.benefits}</p> : null}
      </div>
    </Card>
  );
}
